import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    updateProfile,
    sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from './config';

/**
 * Register a new user with email and password.
 * Also creates a user document in Firestore with additional user data.
 * 
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {Object} userData - Additional user data (firstName, lastName, contactNumber, etc.)
 * @returns {Promise<Object>} - The created user object
 */
export async function signUp(email, password, userData) {
    if (!isFirebaseConfigured || !auth) {
        return { user: null, error: 'Firebase is not configured. Please set up your Firebase credentials.' };
    }

    let user = null;

    // Step 1: Create user with email and password
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
    } catch (error) {
        return { user: null, error: getErrorMessage(error.code) };
    }

    // Step 2: Update the user's display name (non-blocking for sign-up success)
    try {
        await updateProfile(user, {
            displayName: `${userData.firstName} ${userData.lastName}`
        });
    } catch (error) {
        console.error('Failed to update user profile:', error);
        // Continue - profile update failure shouldn't block sign-up
    }

    // Step 3: Create user document in Firestore (non-blocking for sign-up success)
    if (db) {
        try {
            await setDoc(doc(db, 'users', user.uid), {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: email,
                contactNumber: userData.contactNumber,
                agreeToPromotions: userData.agreeToPromotions || false,
                promotionalEmails: userData.agreeToPromotions || false,
                role: 'customer', // Default role for all new registrations
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Failed to create user document in Firestore:', error);
            // Continue - Firestore write failure shouldn't block sign-up
            // The user is already authenticated and can use the app
        }
    }

    // Step 4: Send verification email
    try {
        await sendEmailVerification(user);
    } catch (error) {
        console.error('Failed to send verification email:', error);
        // Continue - verification email failure shouldn't block sign-up
    }

    // Step 5: Sign out the user - they need to verify email before logging in
    // This ensures a clean state for the verification flow
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Failed to sign out after signup:', error);
    }

    return { user, error: null };
}

/**
 * Sign in an existing user with email and password.
 * Requires email to be verified before allowing login.
 * 
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} - The signed in user object
 */
export async function logIn(email, password) {
    if (!isFirebaseConfigured || !auth) {
        return { user: null, error: 'Firebase is not configured. Please set up your Firebase credentials.' };
    }

    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Check if email is verified
        if (!user.emailVerified) {
            // Sign out the user since they haven't verified their email
            await signOut(auth);
            return { 
                user: null, 
                error: 'Please verify your email before logging in. Check your inbox for the verification link.',
                needsVerification: true,
                email: email
            };
        }

        if (db) {
            const [userDocSnap, adminDocSnap] = await Promise.all([
                getDoc(doc(db, 'users', user.uid)),
                getDoc(doc(db, 'admins', user.uid)),
            ]);

            if (!userDocSnap.exists() && !adminDocSnap.exists()) {
                await signOut(auth);
                return {
                    user: null,
                    error: 'This account no longer has access. Please contact support if you think this is a mistake.',
                };
            }
        }

        return { user, error: null };
    } catch (error) {
        return { user: null, error: getErrorMessage(error.code) };
    }
}

/**
 * Resend verification email to the current user or a specified email.
 * 
 * @param {string} email - User's email address
 * @param {string} password - User's password (needed to sign in if not already signed in)
 * @returns {Promise<Object>} - Success status
 */
export async function resendVerificationEmail(email, password) {
    if (!isFirebaseConfigured || !auth) {
        return { error: 'Firebase is not configured. Please set up your Firebase credentials.' };
    }

    try {
        let user = auth.currentUser;
        let needsSignOut = false;

        // If there's no current user or the current user's email doesn't match,
        // we need to sign in temporarily
        if (!user || user.email !== email) {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            user = userCredential.user;
            needsSignOut = true;
        }

        if (user.emailVerified) {
            if (needsSignOut) {
                await signOut(auth);
            }
            return { error: 'Your email is already verified. You can log in now.' };
        }

        await sendEmailVerification(user);
        
        // Sign out after sending the email (only if we signed in temporarily)
        if (needsSignOut) {
            await signOut(auth);
        }
        
        return { error: null, message: 'Verification email sent successfully!' };
    } catch (error) {
        // Provide a more specific message for email rate limiting
        if (error.code === 'auth/too-many-requests') {
            return { error: 'Too many verification emails sent. Please wait a few minutes before trying again.' };
        }
        return { error: getErrorMessage(error.code) };
    }
}

/**
 * Sign out the current user.
 * 
 * @returns {Promise<Object>} - Success status
 */
export async function logOut() {
    if (!isFirebaseConfigured || !auth) {
        return { error: 'Firebase is not configured.' };
    }

    try {
        await signOut(auth);
        return { error: null };
    } catch (error) {
        return { error: getErrorMessage(error.code) };
    }
}

/**
 * Send a password reset email to the specified email address.
 * 
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - Success status
 */
export async function resetPassword(email) {
    if (!isFirebaseConfigured || !auth) {
        return { error: 'Firebase is not configured. Please set up your Firebase credentials.' };
    }

    try {
        await sendPasswordResetEmail(auth, email);
        return { error: null };
    } catch (error) {
        return { error: getErrorMessage(error.code) };
    }
}



/**
 * Convert Firebase error codes to user-friendly messages.
 * 
 * @param {string} errorCode - Firebase error code
 * @returns {string} - User-friendly error message
 */
function getErrorMessage(errorCode) {
    switch (errorCode) {
        case 'auth/email-already-in-use':
            return 'This email is already registered. Please try logging in instead.';
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';
        case 'auth/operation-not-allowed':
            return 'Email/password accounts are not enabled. Please contact support.';
        case 'auth/weak-password':
            return 'Password should be at least 8 characters with uppercase, lowercase letters, and a symbol.';
        case 'auth/user-disabled':
            return 'This account has been disabled. Please contact support.';
        case 'auth/user-not-found':
            return 'No account found with this email address.';
        case 'auth/wrong-password':
            return 'Incorrect password. Please try again.';
        case 'auth/invalid-credential':
            return 'Invalid email or password. Please try again.';
        case 'auth/too-many-requests':
            return 'Too many failed attempts. Please try again later.';
        case 'auth/network-request-failed':
            return 'Network error. Please check your connection and try again.';
        default:
            return 'An error occurred. Please try again.';
    }
}

/* ==================== Phone Number Validation ==================== */

/**
 * Philippine phone number validation regex.
 * Accepts formats:
 * - +639XXXXXXXXX (international format)
 * - 09XXXXXXXXX (local format)
 * - 9XXXXXXXXX (without leading zero)
 */
const PH_PHONE_REGEX = /^(?:\+63|0)?9\d{9}$/;

/**
 * Validates if a phone number is a valid Philippine mobile number.
 * 
 * @param {string} phoneNumber - The phone number to validate
 * @returns {Object} - { isValid: boolean, message: string, formattedNumber: string }
 */
export function validatePhilippineNumber(phoneNumber) {
    // Remove all non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    if (!cleaned) {
        return {
            isValid: false,
            message: 'Please enter your phone number.',
            formattedNumber: null
        };
    }

    // Check if it matches Philippine format
    if (!PH_PHONE_REGEX.test(cleaned)) {
        return {
            isValid: false,
            message: 'Please enter a valid Philippine mobile number (e.g., 09171234567).',
            formattedNumber: null
        };
    }

    // Format to international format (+63)
    let formattedNumber;
    if (cleaned.startsWith('+63')) {
        formattedNumber = cleaned;
    } else if (cleaned.startsWith('0')) {
        formattedNumber = '+63' + cleaned.substring(1);
    } else if (cleaned.startsWith('9')) {
        formattedNumber = '+63' + cleaned;
    } else {
        formattedNumber = cleaned;
    }

    return {
        isValid: true,
        message: '',
        formattedNumber
    };
}
